/**
 * Date utilities that use local timezone instead of UTC.
 * These should be used instead of toISOString().split('T')[0] for date comparisons.
 */

/**
 * Get a date string in YYYY-MM-DD format using local timezone
 * @param {Date} date - The date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function getLocalDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date string in YYYY-MM-DD format using local timezone
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getTodayString() {
  return getLocalDateString(new Date());
}

/**
 * Get tomorrow's date string in YYYY-MM-DD format using local timezone
 * @returns {string} Tomorrow's date in YYYY-MM-DD format
 */
export function getTomorrowString() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return getLocalDateString(tomorrow);
}

/**
 * Get the week boundaries (Monday-Sunday) for a given date
 * @param {Date} date - The date to get week bounds for
 * @returns {{ start: Date, end: Date }} Week start (Monday) and end (Sunday)
 */
export function getWeekBounds(date) {
  const d = new Date(date);
  const day = d.getDay();
  // Adjust so Monday is 0, Sunday is 6
  const diff = day === 0 ? -6 : 1 - day;

  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return { start: monday, end: sunday };
}

/**
 * Get the ISO week string for a given date (e.g., "2026-W02")
 * Uses Monday-Sunday week boundaries
 * @param {Date} date - The date to get week string for
 * @returns {string} ISO week string
 */
export function getWeekString(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  // Get Thursday of the current week (ISO week date system)
  const day = d.getDay();
  const diff = day === 0 ? -3 : 4 - day;
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + diff);

  // Get first Thursday of the year
  const jan1 = new Date(thursday.getFullYear(), 0, 1);
  const jan1Day = jan1.getDay();
  const firstThursday = new Date(jan1);
  if (jan1Day <= 4) {
    firstThursday.setDate(jan1.getDate() + (4 - jan1Day));
  } else {
    firstThursday.setDate(jan1.getDate() + (11 - jan1Day));
  }

  // Calculate week number
  const weekNum = Math.ceil((thursday - firstThursday) / (7 * 24 * 60 * 60 * 1000)) + 1;

  return `${thursday.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

/**
 * Check if a date is within the current week (Monday-Sunday)
 * @param {Date} date - The date to check
 * @returns {boolean} True if date is within current week
 */
export function isCurrentWeek(date) {
  const now = new Date();
  const { start, end } = getWeekBounds(now);
  const d = new Date(date);
  return d >= start && d <= end;
}
